import React, { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, limit, orderBy, query, setDoc, startAfter, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functionsAsaas } from '../firebase';
import ModalReautenticacao from './ModalReautenticacao';
import HistoricoFaturas from './HistoricoFaturas';

const HISTORICO_POR_PAGINA = 5;

function formatarValorMonetario(valor) {
  return Number(valor || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function formatarDataPlano(valor) {
  if (!valor) return '';

  try {
    const dataCalendario = typeof valor === 'string' && /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);
    if (dataCalendario) {
      const [, ano, mes, dia] = dataCalendario;
      const dataValidacao = new Date(`${valor}T00:00:00Z`);
      if (Number.isNaN(dataValidacao.getTime()) || dataValidacao.toISOString().slice(0, 10) !== valor) {
        return '';
      }
      return `${dia}/${mes}/${ano}`;
    }

    const data = typeof valor?.toDate === 'function' ? valor.toDate() : new Date(valor);
    if (Number.isNaN(data.getTime())) return '';
    return data.toLocaleDateString('pt-BR');
  } catch (error) {
    return '';
  }
}

function AbaFinanceiro({ usuarioAtual, configGerais = {}, setAbaAtiva }) {
  const [dadosFiscais, setDadosFiscais] = useState({
    razaoSocial: '',
    cpfCnpj: '',
    telefone: '',
    cep: '',
    rua: '',
    bairro: '',
    numero: '',
    cidade: '',
    estado: '',
  });
  const [historico, setHistorico] = useState([]);
  const [ultimoDocumentoHistorico, setUltimoDocumentoHistorico] = useState(null);
  const [temMaisHistorico, setTemMaisHistorico] = useState(false);
  const [carregandoMaisHistorico, setCarregandoMaisHistorico] = useState(false);
  const [faturaEmAbertura, setFaturaEmAbertura] = useState('');
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [isCancelando, setIsCancelando] = useState(false);
  const [exibirReautenticacao, setExibirReautenticacao] = useState(false);
  const [dataRenovacaoPlano, setDataRenovacaoPlano] = useState(
    usuarioAtual?.data_fim_ciclo || usuarioAtual?.data_fim_ciclo_plano || '',
  );
  const [consultandoRenovacao, setConsultandoRenovacao] = useState(false);

  const estaInadimplente = usuarioAtual?.status_financeiro === 'inadimplente';
  const exigirReautenticacaoCancelar = configGerais?.reautenticacao_lojista_cancelar_assinatura === true;
  const dataFimCicloPlano = formatarDataPlano(dataRenovacaoPlano);

  const resumoPlanoAtual = (() => {
    if (usuarioAtual?.status_assinatura === 'cancelada_agendada') {
      return dataFimCicloPlano
        ? `Sua assinatura permanecerá ativa até ${dataFimCicloPlano}. Após essa data, a conta voltará ao plano Gratuito.`
        : 'Sua assinatura foi cancelada e será encerrada no fim do ciclo atual.';
    }

    if (usuarioAtual?.plano === 'gratuito') {
      return 'Escolha um plano para ativar sua assinatura.';
    }

    if (dataFimCicloPlano) {
      return `Próxima renovação em ${dataFimCicloPlano}.`;
    }

    if (consultandoRenovacao) {
      return 'Consultando a próxima renovação da assinatura...';
    }

    return 'A data da próxima renovação ainda não está disponível.';
  })();

  const carregarHistorico = async (continuar = false) => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    if (continuar) {
      setCarregandoMaisHistorico(true);
    }

    try {
      const restricoes = [
        ...(usuarioAtual?.perfis_ativos?.includes('admin') ? [] : [where('uid_matriz', '==', uid)]),
        orderBy('data', 'desc'),
      ];

      if (continuar && ultimoDocumentoHistorico) {
        restricoes.push(startAfter(ultimoDocumentoHistorico));
      }

      restricoes.push(limit(HISTORICO_POR_PAGINA));
      const snapshot = await getDocs(query(collection(db, 'faturamento_global'), ...restricoes));
      const itens = snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));

      setHistorico((atual) => (continuar ? [...atual, ...itens] : itens));
      setUltimoDocumentoHistorico(snapshot.docs.at(-1) || null);
      setTemMaisHistorico(itens.length === HISTORICO_POR_PAGINA);
    } catch (error) {
      console.error('Erro ao carregar histórico financeiro:', error);
      if (continuar) {
        alert('Não foi possível carregar mais pagamentos agora. Tente novamente.');
      }
    } finally {
      if (continuar) {
        setCarregandoMaisHistorico(false);
      }
    }
  };

  const abrirFatura = async (item) => {
    const janelaFatura = window.open('', '_blank');
    if (!janelaFatura) {
      alert('Permita a abertura de novas guias para visualizar a fatura.');
      return;
    }

    janelaFatura.opener = null;
    setFaturaEmAbertura(item.id);

    try {
      let linkPagamento = String(item.invoiceUrl || '').trim();

      if (!linkPagamento) {
        const obterLinkFaturaAsaas = httpsCallable(functionsAsaas, 'obterLinkFaturaAsaas');
        const resposta = await obterLinkFaturaAsaas({ idFaturamento: item.id });
        linkPagamento = String(resposta?.data?.linkPagamento || '').trim();
        setHistorico((atual) => atual.map((registro) => (
          registro.id === item.id ? { ...registro, invoiceUrl: linkPagamento } : registro
        )));
      }

      if (!linkPagamento) {
        throw new Error('O link desta fatura não está disponível.');
      }

      janelaFatura.location.replace(linkPagamento);
    } catch (error) {
      janelaFatura.close();
      alert(error.message || 'Não foi possível abrir a fatura agora.');
    } finally {
      setFaturaEmAbertura('');
    }
  };

  useEffect(() => {
    setDataRenovacaoPlano(usuarioAtual?.data_fim_ciclo || usuarioAtual?.data_fim_ciclo_plano || '');
  }, [usuarioAtual?.data_fim_ciclo, usuarioAtual?.data_fim_ciclo_plano]);

  useEffect(() => {
    const carregarDadosFinanceiros = async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) return;

      try {
        const userRef = doc(db, 'users', uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const dados = userDoc.data();
          setDadosFiscais({
            razaoSocial: dados.razaoSocial || '',
            cpfCnpj: dados.cpfCnpj || '',
            telefone: dados.telefone || '',
            cep: dados.cep || '',
            rua: dados.rua || '',
            bairro: dados.bairro || '',
            numero: dados.numero || '',
            cidade: dados.cidade || '',
            estado: dados.estado || '',
          });
        }

        await carregarHistorico();
      } catch (error) {
        console.error('Erro ao carregar dados financeiros:', error);
      } finally {
        setLoading(false);
      }
    };

    carregarDadosFinanceiros();
  }, []);

  useEffect(() => {
    let ativo = true;

    const carregarRenovacaoAtual = async () => {
      if (
        !auth.currentUser?.uid ||
        usuarioAtual?.id_usuario_pai ||
        usuarioAtual?.plano === 'gratuito' ||
        !usuarioAtual?.asaas_subscription_id ||
        (usuarioAtual?.data_fim_ciclo || usuarioAtual?.data_fim_ciclo_plano)
      ) {
        return;
      }

      setConsultandoRenovacao(true);
      try {
        const obterResumoAssinaturaAsaas = httpsCallable(functionsAsaas, 'obterResumoAssinaturaAsaas');
        const resposta = await obterResumoAssinaturaAsaas();
        const dados = resposta?.data || {};
        const dataAtualizada = dados.data_fim_ciclo || dados.data_fim_ciclo_plano || '';

        if (ativo && dataAtualizada) {
          setDataRenovacaoPlano(dataAtualizada);
        }
      } catch (error) {
        console.error('Erro ao consultar renovação da assinatura:', error);
      } finally {
        if (ativo) {
          setConsultandoRenovacao(false);
        }
      }
    };

    carregarRenovacaoAtual();

    return () => {
      ativo = false;
    };
  }, [
    usuarioAtual?.asaas_subscription_id,
    usuarioAtual?.data_fim_ciclo,
    usuarioAtual?.data_fim_ciclo_plano,
    usuarioAtual?.id_usuario_pai,
    usuarioAtual?.plano,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDadosFiscais((prev) => ({ ...prev, [name]: value }));
  };

  const salvarDados = async () => {
    setSalvando(true);
    try {
      const uid = auth.currentUser?.uid;
      await setDoc(doc(db, 'users', uid), dadosFiscais, { merge: true });
      alert('Dados atualizados com sucesso!');
    } catch (error) {
      alert(`Erro ao salvar: ${error.message}`);
    } finally {
      setSalvando(false);
    }
  };

  const executarCancelamentoAssinatura = async () => {
    setIsCancelando(true);
    try {
      const cancelarAssinaturaAsaas = httpsCallable(functionsAsaas, 'cancelarAssinaturaAsaas');

      await cancelarAssinaturaAsaas();
      alert('Sua solicitação de cancelamento foi recebida. A assinatura não será renovada no próximo mês.');
    } catch (error) {
      alert(`Erro ao processar cancelamento: ${error.message}`);
    } finally {
      setIsCancelando(false);
    }
  };

  const handleCancelarAssinatura = async () => {
    const confirmou = window.confirm(
      'Tem certeza que deseja cancelar a assinatura? Você continuará tendo acesso aos recursos premium até o último dia do ciclo pago. Após essa data, sua conta retornará ao plano Gratuito.',
    );
    if (!confirmou) return;

    if (exigirReautenticacaoCancelar) {
      setExibirReautenticacao(true);
      return;
    }

    await executarCancelamentoAssinatura();
  };

  if (loading) {
    return <div>Carregando painel financeiro...</div>;
  }

  return (
    <div className="routine-panel">
      {isCancelando && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.38)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            zIndex: 9997,
          }}
        >
          <div
            className="routine-surface"
            style={{
              width: '100%',
              maxWidth: '420px',
              boxShadow: '0 20px 45px rgba(0,0,0,0.18)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: '54px',
                height: '54px',
                borderRadius: '999px',
                margin: '0 auto 16px auto',
                backgroundColor: '#fff4eb',
                color: '#d35400',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                fontWeight: 'bold',
              }}
            >
              ...
            </div>
            <h3 style={{ marginTop: 0, marginBottom: '10px', color: '#1f2937' }}>Processando o cancelamento...</h3>
            <p style={{ margin: 0, color: '#6b7280', lineHeight: '1.6' }}>
              Estamos atualizando sua assinatura e registrando o fim do ciclo atual. Não atualize a página.
            </p>
          </div>
        </div>
      )}

      <ModalReautenticacao
        aberto={exibirReautenticacao}
        titulo="Confirmação reforçada"
        descricao="Confirme sua senha atual antes de cancelar a assinatura desta loja."
        textoBotao="Confirmar cancelamento"
        onFechar={() => setExibirReautenticacao(false)}
        onConfirmado={executarCancelamentoAssinatura}
      />

      {estaInadimplente && (
        <div className="routine-callout routine-callout--danger" style={{ marginBottom: '24px' }}>
          <h3 className="routine-callout__title">Sistema temporariamente suspenso</h3>

          {usuarioAtual?.id_usuario_pai ? (
            <>
              <p className="routine-callout__text">
                O acesso desta filial foi pausado devido a pendências na conta principal.
              </p>
              <p className="routine-callout__text" style={{ marginTop: '10px', fontWeight: 700, color: '#7f1d1d' }}>
                Entre em contato com a administração da rede para normalizar o sistema.
              </p>
            </>
          ) : (
            <>
              <p className="routine-callout__text">
                Identificamos uma fatura em atraso. O acesso da sua loja
                {(usuarioAtual?.plano === 'ouro' || usuarioAtual?.plano === 'diamante')
                  ? ' e de todas as suas filiais '
                  : ' '}
                está bloqueado para novas postagens.
              </p>
              {usuarioAtual?.link_fatura_pendente ? (
                <a
                  href={usuarioAtual.link_fatura_pendente}
                  target="_blank"
                  rel="noreferrer"
                  className="routine-btn routine-btn--danger"
                  style={{ display: 'inline-flex', marginTop: '16px', textDecoration: 'none' }}
                >
                  Pagar fatura
                </a>
              ) : (
                <p className="routine-callout__text" style={{ marginTop: '10px', fontWeight: 700 }}>
                  Verifique seu e-mail ou SMS para acessar o link de pagamento.
                </p>
              )}
              <p className="routine-callout__text" style={{ marginTop: '16px', fontSize: '13px' }}>
                O sistema será liberado automaticamente alguns minutos após a confirmação do pagamento.
              </p>
            </>
          )}
        </div>
      )}

      {!usuarioAtual?.id_usuario_pai && (
        <div className="routine-callout routine-callout--warm" style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
            <div>
              <h3 className="routine-callout__title" style={{ marginBottom: '6px' }}>
                Plano atual: <span style={{ color: '#d97706', textTransform: 'uppercase' }}>{usuarioAtual?.plano || 'gratuito'}</span>
              </h3>
              <p className="routine-callout__text">
                {resumoPlanoAtual}
              </p>
            </div>

            <div className="routine-actions">
              {setAbaAtiva && (
                <button
                  type="button"
                  onClick={() => setAbaAtiva('planos')}
                  disabled={estaInadimplente}
                  title={estaInadimplente ? 'Regularize o pagamento para alterar o plano' : ''}
                  className="routine-btn routine-btn--primary"
                >
                  {usuarioAtual?.status_assinatura === 'cancelada_agendada'
                    ? 'Reativar ou mudar plano'
                    : usuarioAtual?.plano === 'gratuito'
                      ? 'Assinar plano'
                      : 'Mudar de plano'}
                </button>
              )}

              {usuarioAtual?.asaas_subscription_id && usuarioAtual?.status_assinatura !== 'cancelada_agendada' && (
                <button
                  type="button"
                  onClick={handleCancelarAssinatura}
                  disabled={isCancelando || estaInadimplente}
                  title={estaInadimplente ? 'Regularize o pagamento para cancelar' : ''}
                  className="routine-btn routine-btn--danger"
                >
                  {isCancelando ? 'Processando...' : 'Cancelar assinatura'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <details className="routine-details">
        <summary className="routine-details__summary">
          Dados cadastrais e de faturamento
          {dadosFiscais.razaoSocial ? ` - ${dadosFiscais.razaoSocial} (${dadosFiscais.cpfCnpj})` : ''}
        </summary>

        <div className="routine-details__content">
          <div className="routine-grid-2">
            <div className="routine-field">
              <label className="routine-label">Razão social / Nome completo</label>
              <input className="routine-input" name="razaoSocial" value={dadosFiscais.razaoSocial} onChange={handleChange} />
            </div>
            <div className="routine-field">
              <label className="routine-label">CPF / CNPJ</label>
              <input className="routine-input" name="cpfCnpj" value={dadosFiscais.cpfCnpj} onChange={handleChange} />
            </div>
            <div className="routine-field">
              <label className="routine-label">Telefone</label>
              <input className="routine-input" name="telefone" value={dadosFiscais.telefone} onChange={handleChange} />
            </div>
            <div className="routine-field">
              <label className="routine-label">CEP</label>
              <input className="routine-input" name="cep" value={dadosFiscais.cep} onChange={handleChange} />
            </div>
            <div className="routine-field" style={{ gridColumn: '1 / -1' }}>
              <label className="routine-label">Rua</label>
              <input className="routine-input" name="rua" value={dadosFiscais.rua} onChange={handleChange} />
            </div>
            <div className="routine-field">
              <label className="routine-label">Número</label>
              <input className="routine-input" name="numero" value={dadosFiscais.numero} onChange={handleChange} />
            </div>
            <div className="routine-field">
              <label className="routine-label">Bairro</label>
              <input className="routine-input" name="bairro" value={dadosFiscais.bairro} onChange={handleChange} />
            </div>
            <div className="routine-field">
              <label className="routine-label">Cidade</label>
              <input className="routine-input" name="cidade" value={dadosFiscais.cidade} onChange={handleChange} />
            </div>
            <div className="routine-field">
              <label className="routine-label">Estado</label>
              <input className="routine-input" name="estado" value={dadosFiscais.estado} onChange={handleChange} />
            </div>
          </div>

          <div className="routine-actions" style={{ marginTop: '18px' }}>
            <button type="button" onClick={salvarDados} disabled={salvando} className="routine-btn routine-btn--primary">
              {salvando ? 'Salvando...' : 'Salvar dados'}
            </button>
          </div>
        </div>
      </details>

      <div style={{ marginTop: '20px' }}>
        <div className="routine-toolbar" style={{ marginBottom: '14px' }}>
          <div className="routine-title-group">
            <h3 className="routine-section-title">Histórico de pagamentos</h3>
          </div>
        </div>

        {historico.length === 0 ? (
          <div className="routine-surface">
            <div className="routine-empty">Nenhum pagamento registrado no momento.</div>
          </div>
        ) : (
          <div className="routine-table-wrap">
            <table className="routine-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Plano</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th>Link</th>
                </tr>
              </thead>
              <tbody>
                {historico.map((item) => (
                  <tr key={item.id}>
                    <td>{item.data?.toDate ? item.data.toDate().toLocaleDateString('pt-BR') : '---'}</td>
                    <td>{item.descricao}</td>
                    <td>{formatarValorMonetario(item.valor)}</td>
                    <td>
                      <span className="routine-status routine-status--success">Pago</span>
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => abrirFatura(item)}
                        disabled={!item.invoiceUrl && !item.id_asaas}
                        style={{
                          color: '#0f5ca8',
                          fontWeight: 700,
                          border: 'none',
                          background: 'transparent',
                          padding: 0,
                          cursor: !item.invoiceUrl && !item.id_asaas ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {faturaEmAbertura === item.id ? 'Abrindo...' : 'Ver fatura'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {temMaisHistorico && (
          <div className="routine-actions" style={{ marginTop: '16px' }}>
            <button
              type="button"
              onClick={() => carregarHistorico(true)}
              disabled={carregandoMaisHistorico}
              className="routine-btn routine-btn--soft"
            >
              {carregandoMaisHistorico ? 'Carregando...' : 'Carregar mais'}
            </button>
          </div>
        )}
      </div>
      <HistoricoFaturas userId={auth.currentUser?.uid} todos={usuarioAtual?.perfis_ativos?.includes('admin')} somentePendentes onAtualizar={() => carregarHistorico()} />
    </div>
  );
}

export default AbaFinanceiro;
